/*
 * <quantity-input> — replacement for legacy theme.QtySelector.
 * Markup contract: see snippets/quantity-input.liquid.
 *   .js-qty__num            <input> tracking the quantity
 *   .js-qty__adjust--minus  decrement button
 *   .js-qty__adjust--plus   increment button
 *
 * On every change emits a native `change` event on the input AND a
 * `quantity:change` CustomEvent on the host with { value }.
 */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this._onMinus = this._onMinus.bind(this);
    this._onPlus = this._onPlus.bind(this);
    this._onInput = this._onInput.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('.js-qty__num');
    this.minusBtn = this.querySelector('.js-qty__adjust--minus');
    this.plusBtn = this.querySelector('.js-qty__adjust--plus');

    if (!this.input) return;

    this.min = parseInt(this.input.getAttribute('min'), 10) || 1;
    this.max = parseInt(this.input.getAttribute('max'), 10) || Infinity;

    if (this.minusBtn) this.minusBtn.addEventListener('click', this._onMinus);
    if (this.plusBtn) this.plusBtn.addEventListener('click', this._onPlus);
    this.input.addEventListener('change', this._onInput);
  }

  disconnectedCallback() {
    if (this.minusBtn) this.minusBtn.removeEventListener('click', this._onMinus);
    if (this.plusBtn) this.plusBtn.removeEventListener('click', this._onPlus);
    if (this.input) this.input.removeEventListener('change', this._onInput);
  }

  _getQty() {
    const raw = this.input.value;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : this.min;
  }

  _set(qty) {
    const clamped = Math.max(this.min, Math.min(this.max, qty));
    this.input.value = clamped;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.dispatchEvent(new CustomEvent('quantity:change', {
      detail: { value: clamped },
      bubbles: true,
    }));
  }

  _onMinus() { this._set(this._getQty() - 1); }
  _onPlus() { this._set(this._getQty() + 1); }
  _onInput() { this._set(this._getQty()); }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}

export default QuantityInput;
